<template>
  <SubLayout
      :pageTitle="title"
      :pageBreadcrumbs="breadcrumbs"
      :hasBack="true"
  >
    <template #customLayoutContent>
      <Loader :isLoading="getting">
        <div class="p-10">
          <div class="alert alert-danger text-center fs-2" v-if="project.is_oldest" role="alert">
            <i class="fa fa-exclamation-triangle fs-2 text-danger me-2"></i>
            <span v-html="$t('projects.isLateText')"></span>
          </div>
          <div class="card pb-0 px-5 mb-5 pt-2">
              <div class="card-header p-0 mb-5">
                <h3 class="card-title align-items-start flex-column">
                  <span class="card-label fw-bolder text-dark">{{project?.title}} | <span class="fw-light text-gray" v-if="project.category && project.category?.name">{{ project?.category?.name }}</span></span>
                  <span v-if="project.reference" class="text-muted mt-1 fw-bold fs-7">{{ project?.reference }}</span>
                </h3>
                <div class="card-toolbar">
                  <div class="d-flex justify-content-end mb-5">
                    <template v-if="$can(abilities.TASK_PROJECT_CLOSE)">
                      <Button v-if="project.status === projectStatusEnums.SUSPENDED" type="button" :btn-text="$t('button.unsuspend')" :title="$t('button.unsuspend')" btn-class="btn-sm btn-success me-2" iconClass="bi bi-bootstrap-reboot" @click.prevent="suspendOrUnsuspendProject(false)"></Button>
                      <Button v-else type="button" :btn-text="$t('button.suspend')" :title="$t('button.suspend')" btn-class="btn-sm btn-danger me-2" iconClass="fas fa-ban" @click.prevent="suspendOrUnsuspendProject(true)"></Button>
                    </template>
                    <Button v-if="$can(abilities.TASK_PROJECT_CLOSE) && project.status === projectStatusEnums.FINISHED" type="button" :btn-text="$t('button.archive')" :title="$t('button.archive')" btn-class="btn-sm btn-danger me-2" iconClass="fa fa-archive" @click="archiveProject"></Button>
                    <Button v-if="$can(abilities.ADD_PROJECT_USER)" type="new" :btn-text="$t('projects.team')" :title="$t('projects.add_users')" btn-class="btn-sm me-2" iconClass="fas fa-users" @click="openAddUsersModal"></Button>
                    <OutlineButton v-if="$can(abilities.UPDATE_PROJECT)" :btn-text="$t('button.edit')" :title="$t('button.edit')" btn-class="btn-sm me-2" iconClass="fas fa-edit text-primary" @click="openEditModal"></OutlineButton>
                  </div>
                </div>
              </div>
              <div class="d-flex flex-column flex-md-row justify-content-between">
                <ul class="nav nav-custom nav-tabs nav-line-tabs nav-line-tabs-2x border-0 fs-4 fw-bold mb-3 mb-md-0">
                  <li class="nav-item cursor-pointer" @click="clickOnTab('projects-overview')">
                    <a class="nav-link text-active-primary pb-4" :class="{ 'active' : isActive('projects-overview') || isActive('projects-view') }" data-bs-toggle="tab">{{ $t('projects.overview') }}</a>
                  </li>
                  <li class="nav-item cursor-pointer" @click="clickOnTab('projects-fiche')">
                  <a class="nav-link text-active-primary pb-4" :class="{ 'active' : isActive('projects-fiche') }" data-bs-toggle="tab" >{{ $t('projects.fiches.fiche') }}</a>
                  </li>
                  <li class="nav-item cursor-pointer" @click="clickOnTab('projects-information')">
                  <a class="nav-link text-active-primary pb-4" :class="{ 'active' : isActive('projects-information') }" data-bs-toggle="tab">{{ $t('projects.detail') }}</a>
                  </li>
                  <li v-if="$can(abilities.STEP_PROJECT_VIEW)" class="nav-item cursor-pointer" @click="clickOnTab('projects-steps')">
                  <a class="nav-link text-active-primary pb-4" :class="{ 'active' : isActive('projects-steps') }" data-bs-toggle="tab">{{ $t('projects.steps') }}</a>
                  </li>
                  <li v-if="$can(abilities.TASK_PROJECT_VIEW)" class="nav-item cursor-pointer" @click="clickOnTab('projects-tasks')">
                  <a class="nav-link d-flex text-active-primary pb-4"  :class="{ 'active' : isActive('projects-tasks') }" data-bs-toggle="tab">{{ $t('projects.tasks') }}
                    <span v-if="shouldShowTaskAlert" class="badge badge-danger ms-2">
                    <!-- pulse-anim-infinite w-25px h-25px p-2 d-flex align-items-center justify-content-center-->
                    {{ project.alertCount }}
                    </span>
                  </a>
                  </li>
                  <li v-if="$canAccess( moduleAbilities.PROJECT_BUDGET) &&  $can(abilities.PROJECT_BUDGET_VIEW)" class="nav-item cursor-pointer" @click="clickOnTab('projects-budget')">
                  <a class="nav-link text-active-primary pb-4"  :class="{ 'active' : isActive('projects-budget') }" data-bs-toggle="tab">{{ $t('projects.budget') }}</a>
                  </li>
                  <li v-if="$can(abilities.FOLDER_PROJECT_VIEW)" class="nav-item cursor-pointer" @click="clickOnTab('projects-documents')">
                  <a class="nav-link text-active-primary pb-4" :class="{ 'active' : isActive('projects-documents') }" data-bs-toggle="tab">{{ $t('projects.directories') }}</a>
                  </li>
                  <li class="nav-item cursor-pointer" @click="clickOnTab('projects-comments')">
                  <a class="nav-link text-active-primary pb-4" :class="{ 'active' : isActive('projects-comments') }"  data-bs-toggle="tab">{{ $t('projects.comments') }}</a>
                  </li>
                  <li v-if="$canAccess( moduleAbilities.PROJECT_REPORTING)  &&  $can(abilities.PROJECT_REPORTING_VIEW)" class="nav-item cursor-pointer" @click="clickOnTab('projects-reports')">
                  <a class="nav-link text-active-primary pb-4" :class="{ 'active' : isActive('projects-reports') }"  data-bs-toggle="tab">{{ $t('projects.reports.report_title') }}</a>
                  </li>
                  <li v-if="$canAccess( moduleAbilities.PROJECT_CHAT)" class="nav-item cursor-pointer" @click="clickOnTab('projects-chats')">
                    <a class="nav-link text-active-primary pb-4" :class="{ 'active' : isActive('projects-chats') }"  data-bs-toggle="tab">{{ $t('projects.chats') }}</a>
                  </li>
                  <li v-if="$canAccess( moduleAbilities.PROJECT_FEED_NEWS)" class="nav-item cursor-pointer" @click="clickOnTab('projects-actuality')">
                    <a class="nav-link text-active-primary pb-4" :class="{ 'active' : isActive('projects-actuality') }"  data-bs-toggle="tab">{{ $t('menu.feed.title') }}</a>
                  </li>
                  <li v-if="$canAccess( moduleAbilities.NOTARIAL_FORMALITIES)" class="nav-item cursor-pointer" @click="clickOnTab('projects-formality')">
                    <a class="nav-link text-active-primary pb-4" :class="{ 'active' : isActive('projects-formality') }"  data-bs-toggle="tab">{{ $t('projects.formalities') }}</a>
                  </li>
                  <li v-if="true" class="nav-item cursor-pointer" @click="clickOnTab('projects-estates',)">
                    <a class="nav-link text-active-primary pb-4" :class="{ 'active' : isActive('projects-estates') }"  data-bs-toggle="tab">{{ $t('projects.estates.estate') }}</a>
                  </li>
                  <li v-if="true" class="nav-item cursor-pointer" @click="clickOnTab('projects-rdv')">
                    <a class="nav-link text-active-primary pb-4" :class="{ 'active' : isActive('projects-rdv') }"  data-bs-toggle="tab">{{ $t('projects.appointment') }}</a>
                  </li>
                   <li v-if="$canAccess( moduleAbilities.COMMUNICATION_MAIL)" class="nav-item cursor-pointer" @click="clickOnTab('projects-historical')">
                    <a class="nav-link text-active-primary pb-4" :class="{ 'active' : isActive('projects-historical') }"  data-bs-toggle="tab">{{ $t('projects.mails') }}</a>
                  </li>
                </ul>
            </div>
          </div>

          <div class="tab-content" id="myTabContent">
            <Loader :is-loading="redirecting">
              <router-view
                  :project="project"
                  :filter="query"
                  :form="formEdit"
                  :module-id="route.params.id"
                  :module-class="chatProjectModule?.class"
                  :id="route.params.id"
                  @reload="reloadProject"
              />
            </Loader>

          </div>
        </div>
      </Loader>

      <AddUsersModal id="project_add_users_modal" @load='showProject' :form="formAddUsers"/>
      <UpdateProjectModal id="project_edit_modal" @reloadDatatable='showProject' :form="formEdit" />
    </template>
  </SubLayout>
</template>
<script setup> 
import {onMounted, ref, computed, watch} from "vue";
import {useStore} from "vuex";
import SubLayout from "@/views/Layouts/partials/SubLayout.vue";
import {useRouter, useRoute} from 'vue-router';
import TaskIndex from "../Tasks/TaskIndex.vue";
import BudgetIndex from "../Budget/Index.vue";
import OverView from "./components/OverViewComponent"
import Directories from "./components/DirectoriesComponent"
import Comments from "./components/CommentsComponent"
import Detail from "./components/DetailComponent"
import Button from "@/components/Button";
import OutlineButton from "@/components/OutlineButton";
import Step from "./components/StepComponent"
import AddUsersModal from "./components/modals/AddUsersModal"
import UpdateProjectModal from "../../components/Modals/Project/UpdateProjectModal.vue"
import abilities from "@/composables/abilities";
import Loader from "@/components/Loader";
import Reports from "./components/ReportsComponent.vue"
import ChatBox from "@/Modules/Chat/components/ChatBox.vue";
import Feeds from "@/Modules/NewsFeed/views/Feed/Index"
import moduleAbilities from "@/composables/moduleAbilities";
import {useProjectEnums} from "@/Modules/Projects/composables/projectEnums";
import {useSwal} from "@/composables/useSwal";
import {useI18n} from "vue-i18n";
import FormalitiesComponent from "@/Modules/Projects/views/Projects/components/FormalitiesComponent.vue";
import EstateComponent from "./components/EstateComponent.vue";
import FicheComponent from "./components/FicheComponent.vue";
import List from "@/Modules/CRM/views/Appointment/components/List.vue";
import MailHistory from "./components/MailHistory.vue";

import {useModelViewType} from "@/composables/modelViewType";

const { modelViewType } = useModelViewType()
const store = useStore()
const router = useRouter()
const route = useRoute()
const { projectStatusEnums } = useProjectEnums()
const {showSwal} = useSwal()
const { t } = useI18n({ useScope: 'global' })

const title = ref("menu.projects.title")
const breadcrumbs = ref([
  {
    name: 'breadcrumbs.projects.list',
    route: 'projects-index'
  },
  {
    name: 'breadcrumbs.projects.view',
    route: 'projects'
  }
])
const query = ref({
  status: 'all',
  type: 'all',
  start_date: '',
  end_date: '',
  search: '',
  rdv_created: true,
  rdv_guested: true,
  project:  route.params.id
})
const shouldShowTaskAlert = computed(() => !!project.value?.alertCount);
const isActive = (routeName) => route.name === routeName;


const project = ref({})
const getting = ref(true)
const formEdit = ref({})  
const formAddUsers = ref({
  project_id: route.params.id,
  users: [],
  managers: [],
})

const redirecting = computed(() => store.getters['isAppModuleLoaded'])
const currentProject = computed(() => store.getters['projects/getCurrentProject'])
const chatModules = computed(() => store.getters['chat/getConversionModules'])
const chatProjectModule = computed(() => {
  if(chatModules.value && !chatModules.value?.length) return null
  return chatModules.value.find(el => el.value = 'PROJECT')
})

watch(currentProject, (newValue) => {
  if (!newValue) return;

  project.value = newValue
  formAddUsers.value.users = project.value.users.map(user => user.id);
  preparedFormEditData(newValue);

}, { deep: true });

function parseKeyWord(keyWord) {
  if (!keyWord) return [];
  if (typeof keyWord === 'string') {
    try {
      return JSON.parse(keyWord);
    } catch {
      return [keyWord];
    }
  }
  return Array.isArray(keyWord) ? keyWord : [];
}


function clickOnTab(routeName){
  router.push({ name: routeName });
}

function openAddUsersModal(){
  $('#project_add_users_modal').modal('toggle')
}

function openEditModal(){

  $('#project_edit_modal').modal('toggle')
}

function deleteProject(){
 showSwal(
      t('swal.project_delete'),
      t('swal.project_delete_text')
  ).then( async (result) => { 
    if (result.value) {
      store.dispatch('projects/projectDeleting', route.params.id)
          .then((res) => {
            window.toastr['success'](t('notification.project_delete_successful'))
            router.go(-1)
          })
          .catch()
          .finally()
    }
  });
}


function showProject() {
  store.dispatch('projects/showProject', {project_id: route.params.id})
    .then((res) => {
      getting.value = false
    })
}

function reloadProject() {
  store.dispatch('projects/showProject', {project_id: route.params.id})
}

const preparedFormEditData = (data) => {
    if(data) {
      Object.assign(formEdit.value, {
        id: data.id,
        title: data.title,
        reference: data.reference,
        start_date:moment(data.start_date).format('DD-MM-YYYY'),
        end_date: data.end_date ? moment(data.end_date).format('DD-MM-YYYY') : '',
        description: data.description,
        progress: parseFloat(data.progress),
        key_word: parseKeyWord(data.key_word),
        contact_id: data.contact_id,
        category: data.category?.name,
        project_category_id: data.project_category_id,
        manager_id: data.manager_id,
        manager: data.manager?.firstname + ' ' + data.manager?.lastname,
        office_id: data.manager?.office_id,
        status: data.status,
        users: data.users.map(user => user.id),
        managers: data.managers.map(manager => manager.id),
        contacts: data.contacts.map(contact => ({contact_id: contact.contact_id, contact_status_id: contact.status_id})),
        allow_dispatch: data?.budget?.allow_dispatch,
        budget_name: data?.budget?.name,
        forecast_budget : data?.budget?.forecast_budget,
        currency_id :data?.budget?.currency?.id
      });

      if(formEdit.value.description || (formEdit.value.key_word && formEdit.value.key_word.length > 0)) {
        formEdit.value.withDetails = true;
      }
      if(formEdit.value.project_category_id || formEdit.value.users || formEdit.value.manager_id){
        formEdit.value.withCategory = true
      }
  }
}

function archiveProject() {
  showSwal(
      t('swal.project_archive'),
      t('swal.project_archive_text')
  ).then( async (result) => {
    if (result.value) {
      store.dispatch('projects/archiveProject', route.params.id)
          .then((res) => {
            window.toastr['success'](t('notification.project_archived_successful'))
            showProject()
          })
          .catch()
          .finally()
    }
  });

}

function suspendOrUnsuspendProject(isSuspended = true) {
  showSwal(
      isSuspended ? t('swal.project_suspend') : t('swal.project_unsuspend'),
      isSuspended ? t('swal.project_suspend_text') : t('swal.project_unsuspend_text'),
  ).then( async (result) => {
    if (result.value) {
      store.dispatch('projects/suspendOrUnsuspendedProject', route.params.id)
          .then((res) => {
            window.toastr['success'](isSuspended ? t('notification.project_suspended_successful') : t('notification.project_unsuspended_successful'))
            showProject()
          })
          .catch()
          .finally()
    }
  });

}

onMounted(() => {
  showProject()
  store.dispatch('chat/loadConversationsModules')
})
</script>

<style scoped>
.pulse-anim-infinite {
  animation: pulse 1.5s infinite;
}
</style>